// src/services/HolidayService.js

/**
 * Holiday Service
 * Advanced service layer with caching, error handling, and offline support
 * Port of HolidayService.swift for React web application
 */

import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  deleteDoc, 
  query, 
  where, 
  writeBatch 
} from 'firebase/firestore';
import { db } from '../firebase-config';
import { Holiday, HolidayServiceError, HolidayCollection } from '../models/HolidayModels';

// MARK: - Network Monitor
class NetworkMonitor {
  static isOnline() {
    return navigator.onLine;
  }

  static addNetworkListener(callback) {
    window.addEventListener('online', callback);
    window.addEventListener('offline', callback);
  }

  static removeNetworkListener(callback) {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  }
}

// MARK: - Cache Manager
class HolidayCache {
  constructor(maxSize = 50) {
    this.maxSize = maxSize;
    this.cache = new Map();
    this.accessTimes = new Map();
  }

  // Generate cache key
  static generateCacheKey(companyId, month) {
    if (!companyId || !month) return null;
    
    const dateString = Holiday.formatDateForStorage(month);
    return `${companyId}-${dateString}`;
  }

  // Get cached data
  get(key) {
    if (!key || !this.cache.has(key)) return null;
    
    // Update access time for LRU
    this.accessTimes.set(key, new Date());
    return this.cache.get(key);
  }

  // Set cached data
  set(key, data) {
    if (!key || !data) return;

    // Remove oldest entry if cache is full
    if (this.cache.size >= this.maxSize) {
      this._removeOldestEntry();
    }

    this.cache.set(key, data);
    this.accessTimes.set(key, new Date());
  }

  // Check if key exists in cache
  has(key) {
    return this.cache.has(key);
  }

  // Remove entry by key
  delete(key) {
    this.cache.delete(key);
    this.accessTimes.delete(key);
  }

  // Clear all cache
  clear() {
    this.cache.clear();
    this.accessTimes.clear();
  }

  // Remove oldest accessed entry
  _removeOldestEntry() {
    if (this.accessTimes.size === 0) return;

    let oldestKey = null;
    let oldestTime = new Date();

    for (const [key, time] of this.accessTimes.entries()) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.delete(oldestKey);
    }
  }

  // Get cache statistics (for debugging)
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      keys: Array.from(this.cache.keys())
    };
  }
}

// MARK: - Holiday Service Protocol (Interface)
export class HolidayServiceProtocol {
  async fetchHolidaysForMonth(companyId, month) {
    throw new Error('fetchHolidaysForMonth must be implemented');
  }

  async fetchHolidaysForYear(companyId, year) {
    throw new Error('fetchHolidaysForYear must be implemented');
  }

  async createHoliday(companyId, holiday) {
    throw new Error('createHoliday must be implemented');
  }

  async updateHoliday(companyId, holiday) {
    throw new Error('updateHoliday must be implemented');
  }

  async deleteHoliday(companyId, holidayId) {
    throw new Error('deleteHoliday must be implemented');
  }

  async batchCreateHolidays(companyId, holidays) {
    throw new Error('batchCreateHolidays must be implemented');
  }
}

// MARK: - Holiday Service Implementation
export class HolidayService extends HolidayServiceProtocol {
  constructor() {
    super();
    this.cache = new HolidayCache(50);
    this._setupNetworkMonitoring();
  }

  _setupNetworkMonitoring() {
    this.isOnline = NetworkMonitor.isOnline();
    
    const handleNetworkChange = () => {
      this.isOnline = NetworkMonitor.isOnline();
      if (this.isOnline) {
        console.log('🌐 Network connection restored');
      } else {
        console.log('📵 Network connection lost - using cached data');
      }
    };

    NetworkMonitor.addNetworkListener(handleNetworkChange);
  }

  // MARK: - Validation Methods
  _validateCompanyId(companyId) {
    if (!companyId || typeof companyId !== 'string' || companyId.trim().length === 0) {
      throw HolidayServiceError.create(HolidayServiceError.types.INVALID_COMPANY_ID);
    }
    return companyId.trim();
  }

  _validateMonth(month) {
    if (!month || !(month instanceof Date) || isNaN(month.getTime())) {
      throw HolidayServiceError.create(HolidayServiceError.types.INVALID_DATE);
    }
    return month;
  }

  _handleFirestoreError(error) {
    console.error('Firestore error:', error);

    // Map Firebase error codes to our error types
    switch (error.code) {
      case 'permission-denied':
        return HolidayServiceError.create(HolidayServiceError.types.UNAUTHORIZED, error);
      case 'unavailable':
      case 'deadline-exceeded':
        return HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION, error);
      case 'invalid-argument':
      case 'failed-precondition':
        return HolidayServiceError.create(HolidayServiceError.types.DATA_CORRUPTION, error);
      default:
        return HolidayServiceError.create(HolidayServiceError.types.UNKNOWN, error);
    }
  }

  // MARK: - Date Range Calculation
  _calculateDateRangeForMonth(month) {
    const startOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
    const endOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);

    // Add 7-day buffer on each side for edge cases
    const startDate = new Date(startOfMonth);
    startDate.setDate(startDate.getDate() - 7);

    const endDate = new Date(endOfMonth);
    endDate.setDate(endDate.getDate() + 7);

    return { startDate, endDate };
  }

  _calculateDateRangeForYear(year) {
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);

    return { startDate: startOfYear, endDate: endOfYear };
  }

  // MARK: - Core Fetch Methods
  async fetchHolidaysForMonth(companyId, month) {
    try {
      // Input validation
      const validCompanyId = this._validateCompanyId(companyId);
      const validMonth = this._validateMonth(month);

      // Generate cache key
      const cacheKey = HolidayCache.generateCacheKey(validCompanyId, validMonth);
      
      // Check cache first
      const cachedHolidays = this.cache.get(cacheKey);
      if (cachedHolidays) {
        console.log(`📦 Cache hit for ${cacheKey}`);
        return cachedHolidays;
      }

      // Check network connectivity
      if (!this.isOnline) {
        throw HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION);
      }

      // Calculate date range with buffer
      const { startDate, endDate } = this._calculateDateRangeForMonth(validMonth);
      const startDateString = Holiday.formatDateForStorage(startDate);
      const endDateString = Holiday.formatDateForStorage(endDate);

      console.log(`🔍 Fetching holidays for ${validCompanyId} between ${startDateString} and ${endDateString}`);

      // Query Firestore
      const holidaysRef = collection(db, 'companies', validCompanyId, 'holidays');
      const q = query(
        holidaysRef,
        where('dateString', '>=', startDateString),
        where('dateString', '<=', endDateString)
      );

      const querySnapshot = await getDocs(q);
      
      // Parse results
      const holidays = [];
      querySnapshot.forEach((doc) => {
        const holiday = Holiday.fromFirestore(doc.data(), doc.id);
        if (holiday) {
          holidays.push(holiday);
        } else {
          console.warn(`Failed to parse holiday document: ${doc.id}`);
        }
      });

      console.log(`✅ Successfully fetched ${holidays.length} holidays`);

      // Cache successful results
      if (cacheKey) {
        this.cache.set(cacheKey, holidays);
      }

      return holidays;

    } catch (error) {
      // Try to return cached data on error
      const cacheKey = HolidayCache.generateCacheKey(companyId, month);
      const cachedHolidays = this.cache.get(cacheKey);
      
      if (cachedHolidays && cachedHolidays.length > 0) {
        console.warn('🚨 Using cached data due to error:', error.message);
        return cachedHolidays;
      }

      // Handle and rethrow appropriate error
      if (error instanceof HolidayServiceError) {
        throw error;
      } else {
        throw this._handleFirestoreError(error);
      }
    }
  }

  async fetchHolidaysForYear(companyId, year) {
    try {
      // Input validation
      const validCompanyId = this._validateCompanyId(companyId);
      
      if (!year || year < 2000 || year > 2100) {
        throw HolidayServiceError.create(HolidayServiceError.types.INVALID_DATE);
      }

      // Check network connectivity
      if (!this.isOnline) {
        // Try to aggregate from monthly cache
        const cachedYearData = this._getCachedYearData(validCompanyId, year);
        if (cachedYearData.length > 0) {
          return cachedYearData;
        }
        throw HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION);
      }

      // Calculate date range for the entire year
      const { startDate, endDate } = this._calculateDateRangeForYear(year);
      const startDateString = Holiday.formatDateForStorage(startDate);
      const endDateString = Holiday.formatDateForStorage(endDate);

      console.log(`🗓️ Fetching holidays for year ${year}`);

      // Query Firestore for entire year
      const holidaysRef = collection(db, 'companies', validCompanyId, 'holidays');
      const q = query(
        holidaysRef,
        where('dateString', '>=', startDateString),
        where('dateString', '<=', endDateString)
      );

      const querySnapshot = await getDocs(q);
      
      // Parse results
      const holidays = [];
      querySnapshot.forEach((doc) => {
        const holiday = Holiday.fromFirestore(doc.data(), doc.id);
        if (holiday) {
          holidays.push(holiday);
        }
      });

      console.log(`✅ Successfully fetched ${holidays.length} holidays for ${year}`);
      
      // Cache monthly segments for efficiency
      this._cacheHolidaysByMonth(validCompanyId, holidays);

      return Holiday.sortByDate(holidays);

    } catch (error) {
      if (error instanceof HolidayServiceError) {
        throw error;
      } else {
        throw this._handleFirestoreError(error);
      }
    }
  }

  // MARK: - CRUD Operations
  async createHoliday(companyId, holiday) {
    try {
      const validCompanyId = this._validateCompanyId(companyId);
      
      if (!(holiday instanceof Holiday)) {
        throw HolidayServiceError.create(HolidayServiceError.types.DATA_CORRUPTION);
      }

      const validation = holiday.validate();
      if (!validation.isValid) {
        throw HolidayServiceError.create(
          HolidayServiceError.types.DATA_CORRUPTION,
          new Error(validation.error)
        );
      }

      if (!this.isOnline) {
        throw HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION);
      }

      console.log(`➕ Creating holiday: ${holiday.name} on ${holiday.dateString}`);

      const holidayRef = doc(db, 'companies', validCompanyId, 'holidays', holiday.id);
      await setDoc(holidayRef, holiday.toFirestore());

      console.log('✅ Holiday created successfully');

      // Invalidate relevant caches
      this._invalidateMonthCache(validCompanyId, holiday.date);

      return holiday;

    } catch (error) {
      if (error instanceof HolidayServiceError) {
        throw error;
      } else {
        throw this._handleFirestoreError(error);
      }
    }
  }

  async updateHoliday(companyId, holiday) {
    try {
      const validCompanyId = this._validateCompanyId(companyId);
      
      if (!(holiday instanceof Holiday)) {
        throw HolidayServiceError.create(HolidayServiceError.types.DATA_CORRUPTION);
      }

      const validation = holiday.validate();
      if (!validation.isValid) {
        throw HolidayServiceError.create(
          HolidayServiceError.types.DATA_CORRUPTION,
          new Error(validation.error)
        );
      }

      if (!this.isOnline) {
        throw HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION);
      }

      console.log(`✏️ Updating holiday: ${holiday.name}`);

      const holidayRef = doc(db, 'companies', validCompanyId, 'holidays', holiday.id);
      await setDoc(holidayRef, holiday.toFirestore(), { merge: true });

      console.log('✅ Holiday updated successfully');

      // Invalidate relevant caches
      this._invalidateMonthCache(validCompanyId, holiday.date);

      return holiday;

    } catch (error) {
      if (error instanceof HolidayServiceError) {
        throw error;
      } else {
        throw this._handleFirestoreError(error);
      }
    }
  }

  async deleteHoliday(companyId, holidayId) {
    try {
      const validCompanyId = this._validateCompanyId(companyId);
      
      if (!holidayId || typeof holidayId !== 'string') {
        throw HolidayServiceError.create(HolidayServiceError.types.DATA_CORRUPTION);
      }

      if (!this.isOnline) {
        throw HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION);
      }

      console.log(`🗑️ Deleting holiday: ${holidayId}`);

      const holidayRef = doc(db, 'companies', validCompanyId, 'holidays', holidayId);
      await deleteDoc(holidayRef);

      console.log('✅ Holiday deleted successfully');

      // Clear all caches for this company (since we don't know the date)
      this._invalidateAllCaches(validCompanyId);

      return true;

    } catch (error) {
      if (error instanceof HolidayServiceError) {
        throw error;
      } else {
        throw this._handleFirestoreError(error);
      }
    }
  }

  async batchCreateHolidays(companyId, holidays) {
    try {
      const validCompanyId = this._validateCompanyId(companyId);
      
      if (!Array.isArray(holidays) || holidays.length === 0) {
        throw HolidayServiceError.create(HolidayServiceError.types.DATA_CORRUPTION);
      }

      // Validate all holidays
      const validHolidays = [];
      for (const holiday of holidays) {
        if (!(holiday instanceof Holiday)) {
          throw HolidayServiceError.create(HolidayServiceError.types.DATA_CORRUPTION);
        }
        
        const validation = holiday.validate();
        if (!validation.isValid) {
          throw HolidayServiceError.create(
            HolidayServiceError.types.DATA_CORRUPTION,
            new Error(`Invalid holiday "${holiday.name}": ${validation.error}`)
          );
        }
        
        validHolidays.push(holiday);
      }

      if (!this.isOnline) {
        throw HolidayServiceError.create(HolidayServiceError.types.NO_INTERNET_CONNECTION);
      }

      console.log(`📦 Batch creating ${validHolidays.length} holidays`);

      // Use Firestore batch write
      const batch = writeBatch(db);
      
      validHolidays.forEach(holiday => {
        const holidayRef = doc(db, 'companies', validCompanyId, 'holidays', holiday.id);
        batch.set(holidayRef, holiday.toFirestore());
      });

      await batch.commit();

      console.log('✅ Batch holiday creation completed successfully');

      // Invalidate all caches for this company
      this._invalidateAllCaches(validCompanyId);

      return validHolidays;

    } catch (error) {
      if (error instanceof HolidayServiceError) {
        throw error;
      } else {
        throw this._handleFirestoreError(error);
      }
    }
  }

  // MARK: - Cache Management
  _getCachedYearData(companyId, year) {
    const holidays = [];
    
    // Try to get data from monthly caches
    for (let month = 0; month < 12; month++) {
      const monthDate = new Date(year, month, 1);
      const cacheKey = HolidayCache.generateCacheKey(companyId, monthDate);
      const monthlyHolidays = this.cache.get(cacheKey);
      
      if (monthlyHolidays) {
        holidays.push(...Holiday.filterByMonth(monthlyHolidays, year, month));
      }
    }
    
    return holidays;
  }

  _cacheHolidaysByMonth(companyId, holidays) {
    const monthlyGroups = {};
    
    holidays.forEach(holiday => {
      const year = holiday.date.getFullYear();
      const month = holiday.date.getMonth();
      const key = `${year}-${month}`;
      
      if (!monthlyGroups[key]) {
        monthlyGroups[key] = [];
      }
      monthlyGroups[key].push(holiday);
    });
    
    Object.entries(monthlyGroups).forEach(([key, monthlyHolidays]) => {
      const [year, month] = key.split('-').map(Number);
      const monthDate = new Date(year, month, 1);
      const cacheKey = HolidayCache.generateCacheKey(companyId, monthDate);
      
      if (cacheKey) {
        this.cache.set(cacheKey, monthlyHolidays);
      }
    });
  }

  _invalidateMonthCache(companyId, date) {
    const cacheKey = HolidayCache.generateCacheKey(companyId, date);
    if (cacheKey) {
      this.cache.delete(cacheKey);
      console.log(`🗑️ Invalidated cache for ${cacheKey}`);
    }
  }

  _invalidateAllCaches(companyId) {
    const keysToDelete = [];
    
    for (const key of this.cache.cache.keys()) {
      if (key.startsWith(`${companyId}-`)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });
    
    console.log(`🗑️ Invalidated ${keysToDelete.length} cache entries for company ${companyId}`);
  }

  // MARK: - Utility Methods
  getCachedHolidays(companyId, month) {
    const cacheKey = HolidayCache.generateCacheKey(companyId, month);
    return this.cache.get(cacheKey) || [];
  }

  clearCache() {
    this.cache.clear();
    console.log('🗑️ All holiday caches cleared');
  }

  getCacheStats() {
    return this.cache.getStats();
  }
}

// MARK: - Mock Service for Testing
export class MockHolidayService extends HolidayServiceProtocol {
  constructor() {
    super();
    this.mockHolidays = [];
    this.shouldFail = false;
    this.errorToReturn = HolidayServiceError.create(HolidayServiceError.types.UNKNOWN);
    this.delay = 0; // Simulate network delay
  }

  async _simulateDelay() {
    if (this.delay > 0) {
      await new Promise(resolve => setTimeout(resolve, this.delay));
    }
  }

  async fetchHolidaysForMonth(companyId, month) {
    await this._simulateDelay();
    
    if (this.shouldFail) {
      throw this.errorToReturn;
    }
    
    return Holiday.filterByMonth(this.mockHolidays, month.getFullYear(), month.getMonth());
  }

  async fetchHolidaysForYear(companyId, year) {
    await this._simulateDelay();
    
    if (this.shouldFail) {
      throw this.errorToReturn;
    }
    
    return Holiday.filterByYear(this.mockHolidays, year);
  }

  async createHoliday(companyId, holiday) {
    await this._simulateDelay();
    
    if (this.shouldFail) {
      throw this.errorToReturn;
    }
    
    this.mockHolidays.push(holiday);
    return holiday;
  }

  async updateHoliday(companyId, holiday) {
    await this._simulateDelay();
    
    if (this.shouldFail) {
      throw this.errorToReturn;
    }
    
    const index = this.mockHolidays.findIndex(h => h.id === holiday.id);
    if (index !== -1) {
      this.mockHolidays[index] = holiday;
    }
    return holiday;
  }

  async deleteHoliday(companyId, holidayId) {
    await this._simulateDelay();
    
    if (this.shouldFail) {
      throw this.errorToReturn;
    }
    
    const index = this.mockHolidays.findIndex(h => h.id === holidayId);
    if (index !== -1) {
      this.mockHolidays.splice(index, 1);
    }
    return true;
  }

  async batchCreateHolidays(companyId, holidays) {
    await this._simulateDelay();
    
    if (this.shouldFail) {
      throw this.errorToReturn;
    }
    
    this.mockHolidays.push(...holidays);
    return holidays;
  }

  // Test utilities
  setMockData(holidays) {
    this.mockHolidays = [...holidays];
  }

  setFailure(shouldFail, error = null) {
    this.shouldFail = shouldFail;
    if (error) {
      this.errorToReturn = error;
    }
  }

  setDelay(ms) {
    this.delay = ms;
  }

  reset() {
    this.mockHolidays = [];
    this.shouldFail = false;
    this.delay = 0;
  }
}

// MARK: - Service Instance
export const holidayService = new HolidayService();
export default holidayService;